import { z } from 'zod';

export const step1Schema = z.object({
  productOrService: z.enum(['Product', 'Service'], {
    errorMap: () => ({ message: 'Please select whether you offer a Product or Service' }),
  }),
  industry: z.string().min(1, 'Please select an industry'),
  industryId: z.number().optional(),
  description: z.string().optional(),
  serviceDescription: z.string().optional(),
});

export type Step1Data = z.infer<typeof step1Schema>;

export const keyPersonSchema = z.object({
  name: z.preprocess(
    (val) => (typeof val === 'string' ? val.trim() : val),
    z.string().min(1, 'Name is required')
  ),
  familyName: z.preprocess(
    (val) => (typeof val === 'string' ? val.trim() : val),
    z.string().min(1, 'Family name is required')
  ),
  linkedinUrl: z.preprocess(
    (val) => {
      // Convert null, empty string to undefined
      if (val === null || val === '') return undefined;
      if (typeof val === 'string') {
        const cleaned = val.trim().replace(/^@+/, '');
        return cleaned === '' ? undefined : cleaned;
      }
      return val;
    },
    z
      .string()
      .optional()
      .refine(
        (val) => {
          // If empty/undefined, valid (optional field)
          if (!val || val.trim() === '') return true;
          // Must be valid LinkedIn URL
          try {
            new URL(val);
            // Match LinkedIn profile or company URLs
            return /^https?:\/\/(www\.)?linkedin\.com\/(in|company)\/.+/.test(val);
          } catch {
            return false;
          }
        },
        { message: 'Please enter a valid LinkedIn URL (e.g., https://linkedin.com/in/username or https://linkedin.com/company/companyname)' }
      )
  ),
  role: z.preprocess(
    (val) => (typeof val === 'string' ? val.trim() : val),
    z.string().min(1, 'Role is required')
  ),
  pictureUrl: z.preprocess(
    (val) => {
      // Convert null, empty string to undefined
      if (val === null || val === '' || (typeof val === 'string' && val.trim() === '')) {
        return undefined;
      }
      return val;
    },
    z
      .string()
      .optional()
      .refine((val) => !val || /^https?:\/\/.+/.test(val), {
        message: 'Please enter a valid image URL (e.g., https://example.com/image.jpg)',
      })
  ),
});

export const brandColorsSchema = z.object({
  primary: z.string().optional(),
  secondary: z.string().optional(),
  accent: z.string().optional(),
});

export const step2Schema = z.object({
  keyPeople: z.array(keyPersonSchema).optional(),
  brandColors: brandColorsSchema.optional(),
  logoUrl: z
    .string()
    .min(1, 'Company logo is required')
    .refine((val) => {
      // Accept full URLs (http/https)
      if (/^https?:\/\/.+/.test(val)) return true;
      // Accept S3 keys (e.g., "images/user-123.png")
      if (/^[a-zA-Z0-9_\-/]+\.(png|jpg|jpeg|gif|webp|svg)$/i.test(val)) return true;
      // Accept local paths (e.g., "/uploads/images/logo.png")
      if (/^\/uploads\/.+/.test(val)) return true;
      return false;
    }, {
      message: 'Please upload a valid logo image file',
    }),
  brandGuidelines: z.string().optional(),
  country: z.string().min(1, 'Country is required'),
  countryCode: z.string().min(1, 'Country code is required'),
  timezone: z.string().min(1, 'Timezone is required'),
});

export type Step2Data = z.infer<typeof step2Schema>;

export const step3Schema = z.object({
  competitorIds: z.array(z.number()).min(1, 'Please select at least one competitor to continue'),
});

export type Step3Data = z.infer<typeof step3Schema>;

export const step4Schema = z.object({
  linkedinUrl: z
    .string()
    .optional()
    .or(z.literal('')) // Allow empty string
    .refine(
      (val) => {
        // If empty/undefined, valid (optional field)
        if (!val || val.trim() === '') return true;
        // If provided, must be valid LinkedIn company URL
        try {
          new URL(val);
          return /^https?:\/\/(www\.)?linkedin\.com\/company\/.+/.test(val);
        } catch {
          return false;
        }
      },
      { message: 'Please enter a valid LinkedIn company page URL (e.g., https://linkedin.com/company/companyname)' }
    ),
});

export type Step4Data = z.infer<typeof step4Schema>;

export const step5Schema = z.object({
  contentTypeIds: z.array(z.number()).min(1, 'Please select at least one post type to continue'),
  dailyPostCount: z.number().int().min(1, 'Daily post count must be at least 1').max(3, 'Daily post count cannot exceed 3').default(3),
});

export type Step5Data = z.infer<typeof step5Schema>;

export const step6Schema = z.object({
  name: z.string().min(1, 'Target name is required'),
  industry: z.string().min(1, 'Please select a target industry'),
  role: z.string().min(1, 'Target role description is required'),
});

export type Step6Data = z.infer<typeof step6Schema>;

export const PitchStrategyEnum = z.enum([
  'Consultative',
  'ROI-Focused',
  'Authority / Expert',
  'Challenger',
  'Relationship',
  'Direct / Action',
]);

export const step7Schema = z.object({
  strategy: PitchStrategyEnum,
});

export type Step7Data = z.infer<typeof step7Schema>;

export const step8Schema = z.object({
  calendlyLink: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine(
      (val) => {
        if (!val || val.trim() === '') return true;
        try {
          new URL(val);
          return /^https?:\/\/.+/.test(val);
        } catch {
          return false;
        }
      },
      { message: 'Please enter a valid Calendly URL (e.g., https://calendly.com/username/30min)' }
    ),
});

export type Step8Data = z.infer<typeof step8Schema>;

export const wizardSchemas = {
  step1: step1Schema,
  step2: step2Schema,
  step3: step3Schema,
  step4: step4Schema,
  step5: step5Schema,
  step6: step6Schema,
  step7: step7Schema,
  step8: step8Schema,
};

